import { Color } from '@tmh-website/shared/colors';
import { resetButtonStylingCSS } from '@tmh-website/styles/global';
import StyledTypo from '@tmh-website/styles/typo';
import { FormElementThemeColor, StyledFormElements, formElementThemes } from '@tmh-website/ui/FormElements';
import { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

export const FileUploaderContainer = styled.div`
  position: relative;
`;

export const AdjustedLabel = styled(StyledFormElements.Label)`
  margin: 10px 0 4px;
`;

interface DropzoneProps {
  $isDragActive: boolean;
  $hasError?: boolean;
  $themeColor: FormElementThemeColor;
  $isLoading: boolean;
  $isSuccess: boolean;
  $isDisabled: boolean;
}

export const Dropzone = styled.div<DropzoneProps>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 96px;
  padding: 10px ${props => props.theme.wrapperPadding};
  color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
  text-align: center;
  cursor: pointer;
  background-color: ${props => props.theme.color[formElementThemes[props.$themeColor].background.normal]};
  border: 2px dashed ${props => props.theme.color[formElementThemes[props.$themeColor].border.normal]};
  border-radius: ${props => props.theme.borderRadius.large};
  transition: ${props => props.theme.transitionTiming};

  &:hover {
    border-color: ${props => props.theme.color[formElementThemes[props.$themeColor].border.hover]};
  }

  &:active {
    border-color: ${props => props.theme.color[formElementThemes[props.$themeColor].border.active]};
  }

  ${props =>
    props.$isDisabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;

      &:active {
        pointer-events: none;
      }
    `};

  ${props =>
    props.$isDragActive &&
    css`
      border-color: ${props.theme.color[formElementThemes[props.$themeColor].checked.normal]};
      border-style: solid;
    `};

  ${props =>
    props.$hasError &&
    css`
      border-color: ${props => props.theme.color[Color.Red_500_Danger]};

      &:hover {
        border-color: ${props => props.theme.color[Color.Red_600]};
      }
    `};

  ${props =>
    props.$isSuccess &&
    css`
      cursor: not-allowed;
      border-style: solid;
      background-color: ${props.theme.color[formElementThemes[props.$themeColor].surface.normal]};
      box-shadow: inset 0 0 0 2px ${props.theme.color[formElementThemes[props.$themeColor].background.normal]};

      &:active {
        pointer-events: none;
      }
    `};

  ${props =>
    props.$isLoading &&
    css`
      cursor: wait;

      &:active {
        pointer-events: none;
      }
    `};
`;

export const FileList = styled.ul<HTMLAttributes<HTMLUListElement>>`
  padding: 0;
  margin: 6px 0 0;
  list-style: none;
`;

export const FileListEntry = styled(StyledTypo.Typography)<{ $themeColor: FormElementThemeColor }>`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  margin-bottom: 2px;
  line-height: 1;
  color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
  background-color: ${props => props.theme.color[formElementThemes[props.$themeColor].surface.normal]};
  border-radius: ${props => props.theme.borderRadius.small};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FileListEntryRemove = styled.button`
  ${resetButtonStylingCSS};
  padding: 8px;
  margin: -10px;
  transition: color ${props => props.theme.transitionTiming};

  &:hover {
    color: ${props => props.theme.color[Color.Red_500_Danger]};
  }

  > svg {
    display: block;
  }
`;
